// Compute per-player wins/losses for the current session
export const calculateCurrentSessionStats = (history, players) => {
  const stats = {};
  players.forEach((p) => {
    stats[p.name] = { wins: 0, losses: 0 };
  });
  history.forEach((game) => {
    const winners = Array.isArray(game.winningTeam) ? game.winningTeam : [];
    const losers = Array.isArray(game.losingTeam) ? game.losingTeam : [];
    winners.forEach((player) => {
      if (stats[player]) stats[player].wins += 1;
    });
    losers.forEach((player) => {
      if (stats[player]) stats[player].losses += 1;
    });
  });
  return stats;
};

// Compute pairing wins/losses within the given history
export const calculatePairingStats = (history) => {
  const pairingStats = {};
  history.forEach((game) => {
    const winners = Array.isArray(game.winningTeam) ? game.winningTeam : [];
    const losers = Array.isArray(game.losingTeam) ? game.losingTeam : [];
    if (winners.length === 0 && losers.length === 0) return;
    const winningPairKey = winners.slice().sort().join(' & ');
    const losingPairKey = losers.slice().sort().join(' & ');
    pairingStats[winningPairKey] = pairingStats[winningPairKey] || { wins: 0, losses: 0 };
    pairingStats[winningPairKey].wins += 1;
    pairingStats[losingPairKey] = pairingStats[losingPairKey] || { wins: 0, losses: 0 };
    pairingStats[losingPairKey].losses += 1;
  });
  return pairingStats;
};

// Calculate head-to-head wins between two players
export const calculateHeadToHeadWins = (player1, player2, sessionHistory) => {
  if (!sessionHistory || sessionHistory.length === 0) return 0;
  
  let player1Wins = 0;
  
  sessionHistory.forEach((game) => {
    const winningTeam = Array.isArray(game.winningTeam) ? game.winningTeam : [];
    const losingTeam = Array.isArray(game.losingTeam) ? game.losingTeam : [];
    
    // Check if player1 was on winning team and player2 was on losing team
    const player1Won = winningTeam.includes(player1);
    const player2Lost = losingTeam.includes(player2);
    
    // Check if player2 was on winning team and player1 was on losing team
    const player2Won = winningTeam.includes(player2);
    const player1Lost = losingTeam.includes(player1);
    
    if (player1Won && player2Lost) {
      player1Wins += 1;
    } else if (player2Won && player1Lost) {
      player1Wins -= 1;
    }
  });
  
  return player1Wins;
};

// Determine MVP from a sessionStats object
// Optionally takes sessionHistory for head-to-head tiebreaking
export const calculateMVP = (sessionStats, sessionHistory = null) => {
  // First, find players with highest win percentage
  const playersWithStats = [];
  
  Object.entries(sessionStats).forEach(([player, stats]) => {
    const totalGames = stats.wins + stats.losses;
    if (totalGames > 0) {
      const winPercentage = stats.wins / totalGames;
      playersWithStats.push({
        player,
        winPercentage,
        wins: stats.wins,
        stats
      });
    }
  });
  
  if (playersWithStats.length === 0) {
    return null;
  }
  
  // Sort by win percentage (descending), then by wins (descending)
  playersWithStats.sort((a, b) => {
    if (b.winPercentage !== a.winPercentage) {
      return b.winPercentage - a.winPercentage;
    }
    return b.wins - a.wins;
  });
  
  const topWinPercentage = playersWithStats[0].winPercentage;
  
  // Find all players tied for top win percentage
  const tiedPlayers = playersWithStats.filter(
    p => p.winPercentage === topWinPercentage
  );
  
  if (tiedPlayers.length === 1) {
    return tiedPlayers[0].player;
  }
  
  // Multiple players tied - use head-to-head if available
  if (sessionHistory && sessionHistory.length > 0) {
    // Sort tied players by head-to-head wins
    const headToHeadResults = [];
    
    for (let i = 0; i < tiedPlayers.length; i++) {
      const player1 = tiedPlayers[i].player;
      let totalHeadToHeadWins = 0;
      
      for (let j = 0; j < tiedPlayers.length; j++) {
        if (i !== j) {
          const player2 = tiedPlayers[j].player;
          const h2hWins = calculateHeadToHeadWins(player1, player2, sessionHistory);
          totalHeadToHeadWins += h2hWins;
        }
      }
      
      headToHeadResults.push({
        player: player1,
        headToHeadWins: totalHeadToHeadWins
      });
    }
    
    // Sort by head-to-head wins (descending)
    headToHeadResults.sort((a, b) => b.headToHeadWins - a.headToHeadWins);
    
    const topH2H = headToHeadResults[0].headToHeadWins;
    
    // Check if there's still a tie after head-to-head
    const stillTied = headToHeadResults.filter(
      p => p.headToHeadWins === topH2H
    );
    
    if (stillTied.length === 1) {
      return stillTied[0].player;
    }
    
    // Still tied after head-to-head - return "Tie"
    return "Tie";
  }
  
  // No session history available - can't break tie, return "Tie"
  return "Tie";
};

// Given the current game and history length,
// return the player who should serve next. Supports 2-6 player sessions.
// 4–6 player rotations name the server on each game; otherwise it comes from the slots:
// - For 2 players (classic singles): slot1A
// - For 3 players: slot1A serves in games 0-2 (Rotation 1), slot2A serves in games 3-5 (Rotation 2, where slot2A is actually 1B)
// - For 5 players: Uses slot rotation within the 5-game cycle
export const calculateServer = (gameHistoryLength, currentGame, sessionPlayersLength) => {
  if (!currentGame || sessionPlayersLength < 2 || sessionPlayersLength > 6) return 'N/A';

  // Prefer explicit server metadata when provided (e.g., 5-player double switch rotation)
  if (currentGame.server) {
    return currentGame.server;
  }
  if (
    currentGame.servingTeam &&
    typeof currentGame.servingPlayerIndex === 'number' &&
    currentGame[`team${currentGame.servingTeam === 'team2' ? 2 : 1}`]
  ) {
    const teamKey = currentGame.servingTeam === 'team2' ? 'team2' : 'team1';
    const teamPlayers = currentGame[teamKey] || [];
    return teamPlayers[currentGame.servingPlayerIndex] || teamPlayers[0] || 'N/A';
  }
  
  // For games with slot information, determine which slot serves based on game position
  if (currentGame.slot1A !== undefined || currentGame.slot1B !== undefined || currentGame.slot2A !== undefined || currentGame.slot2B !== undefined) {
    let gamesPerRotation;
    let servingSlotIndex;
    
    if (sessionPlayersLength === 2) {
      // 2 players: slot1A serves in game 0, slot1A serves in game 1 (rotate between players)
      return currentGame.slot1A || 'N/A';
    } else if (sessionPlayersLength === 3) {
      // 3 players: 3 games per serving rotation (2 rotations total)
      gamesPerRotation = 3;
      servingSlotIndex = Math.floor(gameHistoryLength / gamesPerRotation) % 2;
      // Rotation 1 (servingSlotIndex 0): slot1A serves
      // Rotation 2 (servingSlotIndex 1): slot2A serves (which is actually 1B in the game structure)
      if (servingSlotIndex === 0) {
        return currentGame.slot1A || 'N/A';
      } else {
        return currentGame.slot2A || 'N/A'; // In rotation 2, slot2A holds the serving player (1B)
      }
    } else if (sessionPlayersLength === 4) {
      // 4 players: 4 games per serving rotation (4 rotations total)
      gamesPerRotation = 4;
      servingSlotIndex = Math.floor(gameHistoryLength / gamesPerRotation) % 4;
      // In the rotation generation:
      // - Rotation 1: slot1A contains the server (1A)
      // - Rotation 2: slot1A contains the server (1B, swapped into slot1A position)
      // - Rotation 3: slot2A contains the server (2A)
      // - Rotation 4: slot2A contains the server (2B, swapped into slot2A position)
      // So we return slot1A for rotations 1-2, and slot2A for rotations 3-4
      if (servingSlotIndex === 0 || servingSlotIndex === 1) {
        return currentGame.slot1A || 'N/A';
      } else {
        return currentGame.slot2A || 'N/A';
      }
    } else if (sessionPlayersLength === 5) {
      // 5 players: Uses slot rotation within the 5-game cycle
      gamesPerRotation = 5;
      const servingRotationCycle = 4; // slot1A, slot1B, slot2A, slot2B
      servingSlotIndex = Math.floor(gameHistoryLength / gamesPerRotation) % servingRotationCycle;
      const slotMap = ['slot1A', 'slot1B', 'slot2A', 'slot2B'];
      const currentServingSlot = slotMap[servingSlotIndex];
      return currentGame[currentServingSlot] || 'N/A';
    } else {
      // 6 players always name a server; this only covers older rotations saved without one
      if (currentGame.slot1A) {
        return currentGame.slot1A;
      }
    }
  }
  
  // Fallback: rotate serving between team1[0] and team2[0] for games without slots
  if (currentGame.team1 && currentGame.team1.length > 0 && currentGame.team2 && currentGame.team2.length > 0) {
    const servingIndex = gameHistoryLength % 2;
    return servingIndex === 0 ? currentGame.team1[0] : currentGame.team2[0];
  }
  
  return 'N/A';
};

